/* eslint-disable max-lines */
import { memo, useState, useCallback, useRef, useEffect } from 'react';

import SelectedProductItem from 'components/SelectedProductItem';
import { useInView } from 'react-intersection-observer';
import { useVirtual } from 'react-virtual';
import { If, Divider } from 'shared';
import { EmptyState } from 'shared/EmptyState';
import { CheckboxField, RadioField } from 'shared/Houston';
import { Loader } from 'shared/Loader';

import ToggleButtonIcon from '../../../shared/ToggleButtonIcon';
import { IOffer, IProduct } from '../../../types/interfaces/product';
import { useProductsList } from '../../ProductsListModal/ProductsListContext';
import ProductOffers from './ProductOffers';
import {
  ChildProductItem,
  Item,
  MainProductItem,
  ChildrenItem,
  ContentLoader as Container,
  InvisibleButtonInfiniteScroll,
  CenterImageIcon,
  CenterLoader
} from './styles';

const ProductsListItem = () => {
  const productListContext = useProductsList();

  const [lastItemIndex, setLastItemIndex] = useState<number>(-1);

  const [showChildren, setShowChildren] = useState<{
    [key: number]: boolean;
  }>({});

  const productHasChildren = useCallback((children?: IProduct[]) => {
    return children ? children.length > 0 : false;
  }, []);

  const [isMaxProductsSelected, setIsMaxProductsSelected] = useState(false);

  const productHasOffer = useCallback((offers: IOffer[] | undefined) => {
    return offers && offers?.length > 0;
  }, []);

  const isProductSelected = useCallback(
    (id: number, isOffer?: boolean) => {
      const selectedProducts = productListContext?.selectedProducts;

      if (!selectedProducts) {
        return false;
      }

      if (isOffer) {
        return !!selectedProducts.find(selectedProduct => selectedProduct.offerId === id);
      }

      return !!selectedProducts.find(selectedProduct => selectedProduct.contentId === id);
    },
    [productListContext?.selectedProducts]
  );

  const handleChange = useCallback(
    (product: IProduct) => {
      const onSelectProduct = productListContext?.onSelectProduct;

      if (!product?.isValid || !onSelectProduct) {
        return;
      }

      onSelectProduct({ contentId: product.contentId });
    },
    [productListContext?.onSelectProduct]
  );

  const handleChangeOffer = useCallback(
    (contentId: number, offer: IOffer) => {
      const onSelectProduct = productListContext?.onSelectProduct;

      if (!offer?.isValid || !onSelectProduct) {
        return;
      }

      onSelectProduct({ contentId, offerId: Number(offer.offerId) });
    },
    [productListContext?.onSelectProduct]
  );

  const { ref: inViewRef, inView } = useInView();

  const setShowChildrenItem = (id: number) => {
    const dataShowChildren = { ...showChildren };
    if (dataShowChildren[id]) {
      delete dataShowChildren[id];
    } else {
      dataShowChildren[id] = true;
    }

    return setShowChildren(dataShowChildren);
  };

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtual({
    size: productListContext?.products?.length || 0,
    parentRef,
    overscan: 25
  });

  const shouldDispatchOnView = useCallback(() => {
    const isFetchingNextPage = productListContext?.isFetchingNextPage;
    const totalProducts = productListContext?.totalProducts;

    const isLastItemInvalid = lastItemIndex === -1;
    const isLastItemBeyondMaxLimit = !totalProducts || lastItemIndex >= totalProducts - 1;

    return !inView || isLastItemInvalid || isLastItemBeyondMaxLimit || isFetchingNextPage;
  }, [inView, lastItemIndex, productListContext?.isFetchingNextPage, productListContext?.totalProducts]);

  useEffect(() => {
    const onInView = productListContext?.onInView;

    if (shouldDispatchOnView() || !onInView) {
      return;
    }

    onInView();
  }, [productListContext?.onInView, shouldDispatchOnView]);

  useEffect(() => {
    const size = rowVirtualizer?.virtualItems?.length;

    setLastItemIndex(rowVirtualizer?.virtualItems[size - 1]?.index || 0);
  }, [rowVirtualizer?.virtualItems, lastItemIndex]);

  useEffect(() => {
    const maxSelectedProducts = productListContext?.maxSelectedProducts;
    const selectedProducts = productListContext?.selectedProducts;

    if (!selectedProducts || !maxSelectedProducts) {
      return;
    }

    setIsMaxProductsSelected(maxSelectedProducts <= selectedProducts?.length);
  }, [productListContext?.maxSelectedProducts, productListContext?.selectedProducts]);

  const shouldDisableCheckbox = useCallback(
    (contentId: number) => {
      if (!productListContext?.disabledSelectionProducts) {
        return;
      }

      const disableSelectionProduct = !!productListContext?.disabledSelectionProducts?.find(
        product => product.contentId === contentId
      );

      return (!isProductSelected(contentId) && isMaxProductsSelected) || disableSelectionProduct;
    },
    [productListContext?.disabledSelectionProducts, isMaxProductsSelected, isProductSelected]
  );

  const renderLoadingOrEmptyState = useCallback(() => {
    if (!productListContext?.isInitialized) {
      return (
        <Container>
          <Loader />
        </Container>
      );
    }

    if (productListContext?.isEmptyProductList) {
      return (
        <Container>
          <EmptyState title='Nenhum produto encontrado' />
        </Container>
      );
    }
  }, [productListContext?.isEmptyProductList, productListContext?.isInitialized]);

  if (!productListContext) {
    return null;
  }

  const { products, multipleSelection } = productListContext;

  return (
    <>
      {renderLoadingOrEmptyState()}

      <If condition={productListContext?.isInitialized}>
        <div ref={parentRef}>
          {rowVirtualizer.virtualItems.map(virtualRow => {
            const product = products[virtualRow.index];

            const isExpanded = showChildren[product?.contentId];

            if (!product?.contentId) {
              return [];
            }

            if (!product.children && !product.offers) {
              return [];
            }

            return (
              <Item key={`product-item-${product.versionId}`}>
                <MainProductItem error={!product.isValid && (product?.children?.length || 0) === 0 ? 1 : 0}>
                  <If
                    condition={
                      !multipleSelection && !productHasChildren(product?.children) && !productHasOffer(product?.offers)
                    }
                  >
                    <CenterImageIcon>
                      <RadioField
                        name='radio-selected-product'
                        value={product.contentId}
                        onChange={() => handleChange(product)}
                        checked={isProductSelected(product.contentId)}
                      />
                    </CenterImageIcon>
                  </If>
                  <If
                    condition={
                      multipleSelection && !productHasChildren(product?.children) && !productHasOffer(product?.offers)
                    }
                  >
                    <CenterImageIcon>
                      <CheckboxField
                        name='selected-product'
                        value={product.contentId}
                        onChange={() => handleChange(product)}
                        checked={isProductSelected(product.contentId)}
                        disabled={shouldDisableCheckbox(product.contentId)}
                      />
                    </CenterImageIcon>
                  </If>
                  <If condition={productHasChildren(product?.children) || productHasOffer(product?.offers)}>
                    <CenterImageIcon>
                      <ToggleButtonIcon
                        show={isExpanded}
                        onClick={() => {
                          setShowChildrenItem(product.contentId);
                        }}
                      />
                    </CenterImageIcon>
                  </If>
                  <SelectedProductItem
                    className='product-list-item'
                    product={product}
                    hideId={productHasChildren(product?.children) || productHasOffer(product?.offers)}
                  />
                </MainProductItem>
                <If condition={isExpanded}>
                  {product?.children?.map(childrenProduct => (
                    <ChildProductItem
                      key={`children-item-${childrenProduct.versionId}`}
                      error={!childrenProduct.isValid ? 1 : 0}
                    >
                      <Divider margin='0 0 8px' />
                      <ChildrenItem
                        key={`children-item-${childrenProduct.versionId}`}
                        multipleSelection={!!multipleSelection}
                      >
                        <If condition={childrenProduct?.offers && childrenProduct?.offers?.length > 0}>
                          <CenterImageIcon>
                            <ToggleButtonIcon
                              show={showChildren[childrenProduct?.contentId]}
                              onClick={() => setShowChildrenItem(childrenProduct.contentId)}
                            />
                          </CenterImageIcon>
                        </If>
                        <If condition={!multipleSelection && !productHasOffer(childrenProduct?.offers)}>
                          <CenterImageIcon>
                            <RadioField
                              name='radio-selected-product'
                              value={childrenProduct.contentId}
                              onChange={() => handleChange(childrenProduct)}
                              checked={isProductSelected(childrenProduct.contentId)}
                            />
                          </CenterImageIcon>
                        </If>
                        <If condition={multipleSelection && !productHasOffer(childrenProduct?.offers)}>
                          <CenterImageIcon>
                            <CheckboxField
                              name='selected-product'
                              value={childrenProduct.contentId}
                              onChange={() => handleChange(childrenProduct)}
                              checked={isProductSelected(childrenProduct.contentId)}
                              disabled={shouldDisableCheckbox(childrenProduct.contentId)}
                            />
                          </CenterImageIcon>
                        </If>

                        <SelectedProductItem className='product-list-item' product={childrenProduct} fontRegular />
                      </ChildrenItem>
                      {showChildren[childrenProduct?.contentId] && (
                        <ProductOffers
                          product={childrenProduct}
                          multipleSelection={multipleSelection}
                          handleChange={handleChangeOffer}
                          isProductSelected={isProductSelected}
                          shouldDisableCheckbox={shouldDisableCheckbox}
                        />
                      )}
                    </ChildProductItem>
                  ))}
                </If>

                <If condition={isExpanded && product.children?.length === 0}>
                  <ProductOffers
                    product={product}
                    multipleSelection={multipleSelection}
                    handleChange={handleChangeOffer}
                    isProductSelected={isProductSelected}
                    shouldDisableCheckbox={shouldDisableCheckbox}
                  />
                </If>
              </Item>
            );
          })}
        </div>
      </If>

      <If condition={productListContext?.isFetchingNextPage}>
        <CenterLoader>
          <Loader />
        </CenterLoader>
      </If>

      <InvisibleButtonInfiniteScroll ref={inViewRef} />
    </>
  );
};

export default memo(ProductsListItem);
